import { HttpClient, HttpEvent, HttpEventType, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { IDocument, IDocumentOwner } from 'app/models/document/document';
import { Observable } from 'rxjs';
import { filter, map, tap, timeout } from 'rxjs/operators';

const jsonOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
  }),
  withCredentials: true
};

/**
 * Single client for the unified Document API. The method names and signatures are kept identical
 * to the StaffWeb document service so the two applications do not drift apart again.
 *
 * The public API authorizes every call against the caller's account affiliations, so a 401 here
 * means the user is not affiliated with the entity that owns the document.
 */
@Injectable({
  providedIn: 'root'
})
export class DocumentService {
  /** 0-100 while an upload is in flight. */
  public uploadProgress = 0;
  /** 0-100 while a download is in flight. */
  public downloadProgress = 0;
  public activeDownloads = 0;

  private readonly api: string = environment.privateApi;
  private readonly documentTimeout: number = 1200000;

  constructor(private readonly http: HttpClient) {}

  getDocuments(owner: IDocumentOwner, category?: string): Observable<IDocument[]> {
    const categoryParam = category ? `&category=${encodeURIComponent(category)}` : '';

    return this.http.get<IDocument[]>(
      `${this.api}Document/ByOwner?ownerType=${owner.ownerType}&ownerId=${owner.ownerId}${categoryParam}`,
      jsonOptions);
  }

  getDocumentsByCategory(category: string): Observable<IDocument[]> {
    return this.http.get<IDocument[]>(`${this.api}Document/ByCategory/${encodeURIComponent(category)}`, jsonOptions);
  }

  uploadDocument(file: File, owner: IDocumentOwner, category?: string, description?: string): Observable<IDocument> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append(this.ownerField(owner.ownerType), String(owner.ownerId));

    if (owner.ownerType === 'FirmApplication' && owner.firmApplicationPersonId) {
      formData.append('FirmApplicationPersonId', String(owner.firmApplicationPersonId));
    }

    if (category) {
      formData.append('DocumentCategory', category);
    }

    if (description) {
      formData.append('FileDescription', description);
    }

    if (file.lastModified) {
      formData.append('LastModified', String(file.lastModified));
    }

    this.uploadProgress = 0;

    return this.http
      .post<IDocument>(`${this.api}Document`, formData, {
        withCredentials: true,
        observe: 'events',
        reportProgress: true
      })
      .pipe(
        timeout(this.documentTimeout),
        tap(event => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            this.uploadProgress = Math.round((event.loaded / event.total) * 100);
          }
        }),
        filter((event: HttpEvent<IDocument>) => event.type === HttpEventType.Response),
        map((event: any) => {
          this.uploadProgress = 0;
          return event.body as IDocument;
        })
      );
  }

  getDocumentContent(id: number): Observable<Blob> {
    this.downloadProgress = 0;
    this.activeDownloads++;

    return this.http
      .get(`${this.api}Document/${id}/Content`, {
        withCredentials: true,
        responseType: 'blob',
        observe: 'events',
        reportProgress: true,
        headers: new HttpHeaders({ Accept: 'application/octet-stream' })
      })
      .pipe(
        timeout(this.documentTimeout),
        tap(event => {
          if (event.type === HttpEventType.DownloadProgress && event.total) {
            this.downloadProgress = Math.round((event.loaded / event.total) * 100);
          }
        }),
        filter((event: HttpEvent<Blob>) => event.type === HttpEventType.Response),
        map((event: any) => {
          this.downloadProgress = 0;
          this.activeDownloads = Math.max(0, this.activeDownloads - 1);
          return event.body as Blob;
        })
      );
  }

  /** Fetches the content and hands it to the browser as a file save. */
  downloadDocument(document: IDocument): Observable<Blob> {
    return this.getDocumentContent(document.id).pipe(
      tap(blob => this.saveBlob(blob, document.fileName))
    );
  }

  deleteDocument(id: number): Observable<boolean> {
    return this.http.delete<boolean>(`${this.api}Document/${id}`, jsonOptions);
  }

  saveBlob(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const anchor = window.document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    window.URL.revokeObjectURL(url);
  }

  private ownerField(ownerType: string): string {
    return `${ownerType}Id`;
  }
}
